import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';

interface SplitScreenProps {
    ads: Array<{ mediaLink: string }>;
    splitCount: number;
}

const SLIDE_DISPLAY_MS = 7500;
const TRANSITION_MS = 500;

const isVideo = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url);

const SplitScreen: React.FC<SplitScreenProps> = ({ ads, splitCount }) => {
    const is2To1Layout = splitCount === 2;
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const isTransitioningRef = useRef(false);
    const innerTimerRef = useRef<NodeJS.Timeout | null>(null);

    const itemsPerSlide = is2To1Layout ? 2 : splitCount;
    const totalSlides = is2To1Layout
        ? Math.floor(ads.length / 2)
        : Math.ceil(ads.length / splitCount);

    // Sync synchronously during render so the inner timer callback always reads
    // the live value — a useEffect would leave it stale for one render cycle
    const totalSlidesRef = useRef(totalSlides);
    totalSlidesRef.current = totalSlides;

    // Reset index if it falls outside the current slide range
    useEffect(() => {
        if (currentSlideIndex >= totalSlides) setCurrentSlideIndex(0);
    }, [totalSlides, currentSlideIndex]);

    // Clean up the inner transition timer on unmount
    useEffect(() => {
        return () => { if (innerTimerRef.current) clearTimeout(innerTimerRef.current); };
    }, []);

    // 7.5-second auto-advance — the effect only re-runs when the displayed slide
    // actually changes, so the timer is never reset mid-display by isTransitioning
    useEffect(() => {
        if (totalSlides <= 1) return;

        const timer = setTimeout(() => {
            if (isTransitioningRef.current) return; // safety guard
            isTransitioningRef.current = true;
            setIsTransitioning(true);

            if (innerTimerRef.current) clearTimeout(innerTimerRef.current);
            innerTimerRef.current = setTimeout(() => {
                // totalSlidesRef.current is always the live value (updated during render)
                setCurrentSlideIndex(prev => (prev + 1) % totalSlidesRef.current);
                setIsTransitioning(false);
                isTransitioningRef.current = false;
                innerTimerRef.current = null;
            }, TRANSITION_MS);
        }, SLIDE_DISPLAY_MS);

        return () => clearTimeout(timer);
    }, [currentSlideIndex, totalSlides]);

    // Returns the items for the current slide — does NOT mutate state during render
    const getCurrentSlideItems = () => {
        if (ads.length === 0) return [];
        const startIndex = currentSlideIndex * itemsPerSlide;
        return ads.slice(startIndex, startIndex + itemsPerSlide);
    };

    const render2To1Layout = () => {
        const currentItems = getCurrentSlideItems();
        if (currentItems.length < 2) return null;

        return (
            <div style={{
                position: 'relative',
                width: '100%',
                height: '100vh',
                backgroundColor: '#000',
                overflow: 'hidden',
                opacity: totalSlides > 1 ? (isTransitioning ? 0 : 1) : 1,
                transition: totalSlides > 1 ? `opacity ${TRANSITION_MS}ms ease-in-out` : 'none',
            }}>
                {/* Main Section */}
                <div style={{
                    position: 'absolute',
                    left: '-10%',
                    top: '-2%',
                    width: '75%',
                    height: '104%',
                    backgroundColor: '#000',
                    overflow: 'hidden',
                    transform: 'skewX(-15deg)',
                    borderRight: '15px solid white',
                    zIndex: 1,
                }}>
                    <div style={{
                        position: 'absolute',
                        left: '10%',
                        top: '2%',
                        width: '100%',
                        height: '100%',
                        transform: 'skewX(15deg) scale(1.1)',
                    }}>
                        {isVideo(currentItems[0].mediaLink) ? (
                            <ReactPlayer
                                url={currentItems[0].mediaLink}
                                playing={true}
                                controls={false}
                                muted={true}
                                loop={true}
                                width="100%"
                                height="100%"
                                style={{ objectFit: 'cover' }}
                            />
                        ) : (
                            <img
                                src={currentItems[0].mediaLink}
                                alt="Main content"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </div>

                {/* Logo */}
                <div style={{
                    position: 'absolute',
                    left: '66%',
                    top: '50%',
                    width: 98,
                    height: 98,
                    transform: 'translate(-49px, -49px) skewX(-15deg)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <img
                        src="/Android.png"
                        alt="Logo"
                        style={{ width: '100%', height: '100%', transform: 'skewX(15deg)' }}
                    />
                </div>

                {/* Side Section */}
                <div style={{
                    position: 'absolute',
                    right: '-10%',
                    top: '-2%',
                    width: '42%',
                    height: '104%',
                    backgroundColor: '#000',
                    overflow: 'hidden',
                    transform: 'skewX(-15deg)',
                    borderLeft: '15px solid white',
                }}>
                    <div style={{
                        position: 'absolute',
                        right: '10%',
                        top: '2%',
                        width: '100%',
                        height: '100%',
                        transform: 'skewX(15deg) scale(1.1)',
                    }}>
                        {isVideo(currentItems[1].mediaLink) ? (
                            <ReactPlayer
                                url={currentItems[1].mediaLink}
                                playing={true}
                                controls={false}
                                muted={true}
                                loop={true}
                                width="100%"
                                height="100%"
                                style={{ objectFit: 'cover' }}
                            />
                        ) : (
                            <img
                                src={currentItems[1].mediaLink}
                                alt="Side content"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderGridLayout = () => {
        const currentItems = getCurrentSlideItems();
        if (currentItems.length === 0) return null;

        const gridColumns = Math.ceil(Math.sqrt(splitCount));

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                height: '100vh',
                opacity: totalSlides > 1 ? (isTransitioning ? 0 : 1) : 1,
                transition: totalSlides > 1 ? `opacity ${TRANSITION_MS}ms ease-in-out` : 'none',
            }}>
                {currentItems.map((ad, index) => (
                    isVideo(ad.mediaLink) ? (
                        <div key={`video-${index}-${ad.mediaLink}`} style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <ReactPlayer
                                url={ad.mediaLink}
                                playing={true}
                                controls={false}
                                muted={true}
                                loop={true}
                                width="100%"
                                height="100%"
                                style={{ objectFit: 'cover' }}
                            />
                        </div>
                    ) : (
                        <div key={`image-${index}-${ad.mediaLink}`} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                alt={`Media ${index}`}
                                src={ad.mediaLink}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        </div>
                    )
                ))}
            </div>
        );
    };

    if (ads.length === 0) return null;

    return is2To1Layout ? render2To1Layout() : renderGridLayout();
};

export default SplitScreen;
